
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Info, Loader2 } from 'lucide-react';

interface Tag {
  id: string;
  name: string;
  article_count: number;
  slug: string;
  description?: string;
}

interface TopicHeatMapProps {
  topics: Tag[];
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setSelectedTopic: (topic: string | null) => void;
  toggleFollowTopic: (topicId: string) => void;
  followedTopics: string[];
}

const TopicHeatMap: React.FC<TopicHeatMapProps> = ({ 
  topics, 
  loading, 
  searchQuery, 
  setSearchQuery,
  setSelectedTopic,
  toggleFollowTopic,
  followedTopics
}) => {
  const navigate = useNavigate();
  const filteredTopics = topics.filter(topic => 
    topic.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSizeClass = (count: number) => {
    if (count > 100) return "text-2xl";
    if (count > 50) return "text-xl";
    if (count > 20) return "text-lg";
    return "text-base";
  };

  const getGrowthColor = (count: number) => {
    if (count > 100) return "bg-red-500";
    if (count > 50) return "bg-orange-500";
    if (count > 20) return "bg-green-500";
    return "bg-blue-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading topics...</span>
      </div>
    );
  }

  if (filteredTopics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px]">
        <p className="text-muted-foreground">No topics found matching your search</p>
        {searchQuery && (
          <Button variant="ghost" onClick={() => setSearchQuery("")} className="mt-2">
            Clear search
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-4 justify-center">
      {filteredTopics.map(topic => {
        const sizeClass = getSizeClass(topic.article_count);
        const colorClass = getGrowthColor(topic.article_count);
        
        return (
          <HoverCard key={topic.id}>
            <HoverCardTrigger asChild>
              <Button 
                variant="ghost" 
                onClick={() => setSelectedTopic(topic.name)}
                className={`${sizeClass} relative group`}
              >
                <span>{topic.name}</span>
                {topic.article_count > 20 && (
                  <span className={`absolute -top-1 -right-1 h-3 w-3 ${colorClass} rounded-full`}></span>
                )}
              </Button>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold">{topic.name}</h4>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <span>{topic.article_count} articles</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFollowTopic(topic.id);
                  }}
                >
                  {followedTopics.includes(topic.id) ? "Following" : "Follow"}
                </Button>
              </div>
              
              {topic.description && (
                <div className="mt-3 bg-muted p-2 rounded-md text-sm">
                  <div className="font-medium flex items-center gap-1 mb-1">
                    <Info className="h-4 w-4" /> Why it matters
                  </div>
                  <p>{topic.description}</p>
                </div>
              )}
              
              <div className="mt-3">
                <Button 
                  variant="link" 
                  className="p-0 h-auto" 
                  onClick={() => navigate(`/tags/${topic.name}`)}
                >
                  View all articles
                </Button>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      })}
    </div>
  );
};

export default TopicHeatMap;
